/** The views of the content screens, as the address bar holds them. A default is not written. */
import { idOf, pageOf, text } from "@/shared/lib/search";

import { AUDIENCES, ITEM_STATES, type Audience, type ItemState } from "../api/types";

/** The tabs of a list: every item, then each state hg-content counts. */
export const ITEM_TABS = ["all", ...ITEM_STATES] as const;
export type ItemTab = (typeof ITEM_TABS)[number];

/** A list of news or of stories: its tab, its filters, its page. */
export interface ItemsSearch {
  state?: ItemState;
  q?: string;
  audience?: Audience;
  area?: string;
  /** News only: stories have no tag. */
  tag?: string;
  page?: number;
}

export function parseItemsSearch(raw: Readonly<Record<string, unknown>>): ItemsSearch {
  const search: ItemsSearch = {};
  const state = ITEM_STATES.find((known) => known === raw["state"]);
  if (state !== undefined) search.state = state;
  const q = text(raw["q"]);
  if (q !== undefined) search.q = q;
  const audience = AUDIENCES.find((known) => known === raw["audience"]);
  if (audience !== undefined) search.audience = audience;
  const area = idOf(raw["area"]);
  if (area !== undefined) search.area = area;
  const tag = idOf(raw["tag"]);
  if (tag !== undefined) search.tag = tag;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  return search;
}

/** The filters of `GET /v2/admin/news` and `/stories`, by their own names. */
export interface ItemsFilter {
  state?: ItemState;
  q?: string;
  audience?: Audience;
  area_id?: string;
  tag_id?: string;
}

export function itemsFilter(search: ItemsSearch): ItemsFilter {
  const filter: ItemsFilter = {};
  if (search.state !== undefined) filter.state = search.state;
  if (search.q !== undefined) filter.q = search.q;
  if (search.audience !== undefined) filter.audience = search.audience;
  if (search.area !== undefined) filter.area_id = search.area;
  if (search.tag !== undefined) filter.tag_id = search.tag;
  return filter;
}

/** The view with another tab: the page starts again, the filters stay. */
export function withTab(search: ItemsSearch, tab: ItemTab): ItemsSearch {
  const { state: _, page: __, ...rest } = search;
  return tab === "all" ? rest : { ...rest, state: tab };
}

/** Where a new legal version starts from: another one's words, or nothing. */
export interface NewVersionSearch {
  from?: string;
}

export function parseNewVersionSearch(raw: Readonly<Record<string, unknown>>): NewVersionSearch {
  const from = idOf(raw["from"]);
  return from === undefined ? {} : { from };
}
